import { useEffect } from "react";
import { Toaster } from "./components/ui/toaster";
import { Toaster as Sonner } from "./components/ui/sonner";
import { TooltipProvider } from "./components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { AuthProvider } from "./components/auth/AuthProvider";
import AuthErrorBoundary from "./components/auth/AuthErrorBoundary";
import Navigation from "./components/Navigation";
import Footer from "./components/Footer";

import { useVisitorTracking } from "./hooks/useVisitorTracking";
import useScrollRestoration from "./hooks/useScrollRestoration";
import { useLocation } from "react-router-dom";
import Index from "./pages/Index";
import About from "./pages/About";
import Regions from "./pages/Regions";
import PrayerMap from "./pages/PrayerMap";
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";
import NotFound from "./pages/NotFound";
import Conferences from "./pages/Conferences";
import ConferenceHub from "./pages/ConferenceHub";
import ConferenceDetail from "./pages/ConferenceDetail";
import ConferenceRegistration from "./pages/ConferenceRegistration";
import PacificNorthwestLive from "./pages/PacificNorthwestLive";
import SoutheastLive from "./pages/SoutheastLive";
import HotelAccommodationGuide from "./pages/HotelAccommodationGuide";
import Contact from "./pages/Contact";
import Give from "./pages/Give";
import WomensMinistry from "./pages/WomensMinistry";
import NewStudents from "./pages/NewStudents";
import KeepInTouch from "./pages/KeepInTouch";
import Gallery from "./pages/Gallery";
import ChatbotWidget from "./components/ChatbotWidget";
import PrayerConferenceModal from "./components/PrayerConferenceModal";
import MobileAppShell from "./components/mobile/MobileAppShell";
import { shouldShowMobileShell } from "./components/mobile/mobileShellRoutes";
import { useNativeBackButton } from "./native/backButton";
import { initNativeShell } from "./native/bootstrap";

import RequireAuth from "./components/auth/RequireAuth";


const queryClient = new QueryClient();

// Normalize a region path param (e.g. "south-east", "Southern Region") to a slug
// the per-region page understands and that matches our /conference/:slug URLs.
const normalizeRegionParam = (raw: string | undefined) =>
  (raw || "").toLowerCase().replace(/region/g, "").replace(/[^a-z0-9]+/g, "").trim();

const RegionRedirect = ({ target, param = "region" }: { target: "conference"; param?: string }) => {
  const params = useParams();
  const value = (params as Record<string, string | undefined>)[param];
  const slug = normalizeRegionParam(value);
  return <Navigate to={slug ? `/${target}/${slug}` : `/${target}`} replace />;
};


const AppContent = () => {
  const location = useLocation();
  const isStandalone = location.pathname.startsWith("/ofuesi-2026-regional-conference/");
  const isDocumentRoute = location.pathname === "/hotel-accommodation-guide" || location.pathname === "/documents/hotel-accommodation-guide";
  const showFooter = !location.pathname.startsWith('/admin/dashboard') && !isStandalone && !isDocumentRoute;
  const showNav = !isStandalone && !isDocumentRoute;
  const showMobileShell = shouldShowMobileShell(location.pathname);

  // Scroll to top on fresh navigation, restore prior position on Back/Forward.
  useScrollRestoration();
  // Android hardware back button (no-op on web).
  useNativeBackButton();

  return (
    <>
      <div
        className={`min-h-screen faith-pattern ${showMobileShell ? "pt-14 pb-16 md:pt-0 md:pb-0 print:pt-0 print:pb-0" : ""}`}
        style={{background: 'var(--background)'}}
      >
        <MobileAppShell />
        {showNav && (
          <div className={showMobileShell ? "hidden md:block print:block" : undefined}>
            <Navigation />
          </div>
        )}
        <main><Routes>
          <Route path="/" element={<Index />} />
          <Route path="/about" element={<About />} />
          <Route path="/regions" element={<Regions />} />
          <Route path="/prayer" element={<PrayerMap />} />
          {/* New Conference hub + per-region subpages */}
          <Route path="/conference" element={<ConferenceHub />} />
          <Route path="/conference/:slug" element={<ConferenceRegistration />} />
           <Route path="/conference/northwest/live" element={<PacificNorthwestLive />} />
          <Route path="/conference/southeast/live" element={<SoutheastLive />} />
          {/* Standalone shareable per-region page (no global nav/footer) */}
          <Route path="/ofuesi-2026-regional-conference/:slug" element={<ConferenceRegistration />} />
          <Route path="/ofuesi-2026-regional-conference/:slug/conference" element={<ConferenceRegistration />} />
          <Route path="/ofuesi-2026-regional-conference/:slug/conference/registration" element={<ConferenceRegistration />} />
          <Route path="/hotel-accommodation-guide" element={<HotelAccommodationGuide />} />
          <Route path="/documents/hotel-accommodation-guide" element={<HotelAccommodationGuide />} />
          {/* Backwards-compatible redirects to the new hub/subpages */}
          <Route path="/conferences" element={<Navigate to="/conference" replace />} />
          <Route path="/conferences/:region" element={<RegionRedirect target="conference" />} />
          <Route path="/registrations" element={<Navigate to="/conference" replace />} />
          <Route path="/registrations/:slug" element={<RegionRedirect target="conference" param="slug" />} />
          <Route path="/register/regional-conference-2026" element={<Navigate to="/conference" replace />} />
          {/* Shareable per-region aliases, e.g. /southern-registration → /conference/southern */}
          {["southern", "eastern", "midwest", "mid-west", "northwest", "north-west", "southwest", "south-west", "southeast", "south-east"].map((r) => (
            <Route key={r} path={`/${r}-registration`} element={<Navigate to={`/conference/${r.replace(/-/g, "")}`} replace />} />
          ))}
          <Route path="/contact" element={<Contact />} />
          <Route path="/give" element={<Give />} />
          <Route path="/womens-ministry" element={<WomensMinistry />} />
          <Route path="/new-students" element={<NewStudents />} />
          <Route path="/keep-in-touch" element={<KeepInTouch />} />
          <Route path="/gallery" element={<Gallery />} />
          <Route path="/admin" element={<AdminLogin />} />
          <Route path="/admin/dashboard/*" element={<RequireAuth><AdminDashboard /></RequireAuth>} />
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
        </Routes></main>
        <ChatbotWidget />
        {location.pathname === "/" && <PrayerConferenceModal onDismissed={() => {}} />}
        {showFooter && <Footer />}
      </div>
    </>
  );
};

const App = () => {
  useVisitorTracking();

  // Safe-area variables + native status bar / splash handling. No-op on web.
  useEffect(() => {
    initNativeShell();
  }, []);

  
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <AuthErrorBoundary>
          <AuthProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <AppContent />
            </BrowserRouter>
          </AuthProvider>
        </AuthErrorBoundary>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
